import { useState, useEffect } from "react";
import { getProdotti, createProdotto, deleteProdotto, updateProdotto, bulkDeleteProdotti } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Box, Search, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ArchivioProdotti() {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProdotto, setEditingProdotto] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    descrizione: "",
    unita_misura: "kg",
    percentuale_sconto: 5,
  });

  useEffect(() => {
    loadProdotti();
  }, []);

  const loadProdotti = async () => {
    try {
      const response = await getProdotti();
      setProdotti(response.data);
      setSelectedIds([]);
    } catch (error) {
      console.error("Error loading prodotti:", error);
      toast.error("Errore nel caricamento dei prodotti");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    try {
      if (editingProdotto) {
        await updateProdotto(editingProdotto.id, formData);
        toast.success("Prodotto aggiornato con successo");
      } else {
        await createProdotto(formData);
        toast.success("Prodotto creato con successo");
      }
      
      setDialogOpen(false);
      setEditingProdotto(null);
      setFormData({ nome: "", descrizione: "", unita_misura: "kg", percentuale_sconto: 5 });
      loadProdotti();
    } catch (error) {
      console.error("Error saving prodotto:", error);
      toast.error("Errore nel salvataggio del prodotto");
    }
  };

  const handleEdit = (prodotto) => {
    setEditingProdotto(prodotto);
    setFormData({
      nome: prodotto.nome,
      descrizione: prodotto.descrizione || "",
      unita_misura: prodotto.unita_misura || "kg",
      percentuale_sconto: prodotto.percentuale_sconto ?? 5,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteProdotto(id);
      toast.success("Prodotto eliminato con successo");
      setDeleteConfirm(null);
      loadProdotti();
    } catch (error) {
      console.error("Error deleting prodotto:", error);
      toast.error("Errore nell'eliminazione del prodotto");
    }
  };

  const handleOpenDialog = () => {
    setEditingProdotto(null);
    setFormData({ nome: "", descrizione: "", unita_misura: "kg", percentuale_sconto: 5 });
    setDialogOpen(true);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredProdotti.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteProdotti(selectedIds);
      toast.success(`${selectedIds.length} prodotti eliminati con successo`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      loadProdotti();
    } catch (error) {
      console.error("Error bulk deleting prodotti:", error);
      toast.error("Errore nell'eliminazione multipla");
    }
  };

  const filteredProdotti = prodotti.filter((p) =>
    p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.descrizione && p.descrizione.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="archivio-prodotti-page" className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Archivio Prodotti
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Gestisci l'anagrafica dei prodotti
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="nuovo-prodotto-btn"
              className="btn-accent rounded-lg"
              onClick={handleOpenDialog}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Prodotto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
                <Box className="w-5 h-5 text-orange-500" />
                {editingProdotto ? "Modifica Prodotto" : "Nuovo Prodotto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="font-heading font-bold text-slate-700">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  data-testid="prodotto-nome-input"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es: Fanghi industriali"
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descrizione" className="font-heading font-bold text-slate-700">
                  Descrizione
                </Label>
                <Textarea
                  id="descrizione"
                  data-testid="prodotto-descrizione-input"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Descrizione del prodotto..."
                  className="rounded-sm"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="unita_misura" className="font-heading font-bold text-slate-700">
                  Unità di Misura
                </Label>
                <Input
                  id="unita_misura"
                  data-testid="prodotto-unita-input"
                  value={formData.unita_misura}
                  onChange={(e) => setFormData({ ...formData, unita_misura: e.target.value })}
                  placeholder="kg"
                  className="rounded-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="percentuale_sconto" className="font-heading font-bold text-slate-700">
                  Percentuale Sconto (%)
                </Label>
                <Input
                  id="percentuale_sconto"
                  data-testid="prodotto-percentuale-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.percentuale_sconto}
                  onChange={(e) => setFormData({ ...formData, percentuale_sconto: parseFloat(e.target.value) || 0 })}
                  placeholder="5"
                  className="rounded-sm"
                />
                <p className="text-xs text-slate-500">Percentuale da applicare nel calcolo netto del report</p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-prodotto-btn"
                  className="btn-primary rounded-sm"
                >
                  {editingProdotto ? "Salva Modifiche" : "Crea Prodotto"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="card-industrial">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-prodotti-input"
              placeholder="Cerca per nome o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="card-industrial">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <Box className="w-5 h-5 text-orange-500" />
              Elenco Prodotti ({filteredProdotti.length})
            </CardTitle>
            {selectedIds.length > 0 && (
              <Button
                data-testid="bulk-delete-prodotti-btn"
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteConfirm(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina Selezionati ({selectedIds.length})
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {filteredProdotti.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">
                        <Checkbox
                          data-testid="select-all-prodotti"
                          checked={filteredProdotti.length > 0 && selectedIds.length === filteredProdotti.length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Nome
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Descrizione
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Unità
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        % Sconto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data Creazione
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProdotti.map((prodotto, index) => (
                      <TableRow 
                        key={prodotto.id}
                        data-testid={`prodotto-row-${index}`}
                        className={`hover:bg-slate-50/50 ${selectedIds.includes(prodotto.id) ? "bg-orange-50" : ""}`}
                      >
                        <TableCell>
                          <Checkbox
                            data-testid={`select-prodotto-${index}`}
                            checked={selectedIds.includes(prodotto.id)}
                            onCheckedChange={(checked) => handleSelectItem(prodotto.id, checked)}
                          />
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {prodotto.nome}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                          {prodotto.descrizione || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 font-mono">
                          {prodotto.unita_misura}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 font-mono font-bold">
                          {prodotto.percentuale_sconto ?? 5}%
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-500">
                          {formatDate(prodotto.created_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-prodotto-${index}`}
                              onClick={() => handleEdit(prodotto)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-prodotto-${index}`}
                              onClick={() => setDeleteConfirm(prodotto)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Box className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  {searchTerm ? "Nessun prodotto trovato" : "Nessun prodotto registrato"}
                </p>
                <p className="text-sm mt-1">
                  {searchTerm ? "Prova con una ricerca diversa" : "Clicca su 'Nuovo Prodotto' per iniziare"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il prodotto <span className="font-bold">{deleteConfirm?.nome}</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-prodotto-btn"
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare <span className="font-bold">{selectedIds.length} prodotti</span>?
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-bulk-delete-prodotti-btn"
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} Prodotti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
